// - - - GENERAL FILTER - - -

import { t } from '@lingui/macro';
import { endOfDay, startOfDay, subDays, subHours, subMonths } from 'date-fns';
import { AlarmsParamsBaseModel } from './Alarms';
import { IntervalOptionMode } from './Graph';
import { HistoryParamsBaseModel } from './History';

export interface FiltersPeriodModel {
  from: string;
  to: string;
}

export enum TimeRangeType {
  Last24hours = 'Last24hours',
  Last7days = 'Last7days',
  Last1month = 'Last1month',
  Last3months = 'Last3months',
  Last6months = 'Last6months',
  Last12months = 'Last12months',
}

export const TimeRangeOptionsFn = (): Record<TimeRangeType, { label: string; range: [Date, Date] }> => ({
  [TimeRangeType.Last24hours]: {
    label: t`Today`,
    range: [subHours(new Date(), 24), endOfDay(new Date())],
  },
  [TimeRangeType.Last7days]: {
    label: t`Last 7 days`,
    range: [startOfDay(subDays(new Date(), 7)), endOfDay(new Date())],
  },
  [TimeRangeType.Last1month]: {
    label: t`Last 30 days`,
    range: [startOfDay(subDays(new Date(), 30)), endOfDay(new Date())],
  },
  [TimeRangeType.Last3months]: {
    label: t`Last 3 months`,
    range: [startOfDay(subMonths(new Date(), 3)), endOfDay(new Date())],
  },
  [TimeRangeType.Last6months]: {
    label: t`Last 6 months`,
    range: [startOfDay(subMonths(new Date(), 6)), endOfDay(new Date())],
  },
  [TimeRangeType.Last12months]: {
    label: t`Last 12 months`,
    range: [startOfDay(subMonths(new Date(), 12)), endOfDay(new Date())],
  },
});

// - - - DASHBOARD - - -

export enum IntervalOptionsType {
  Auto = 'Auto',
  Logger = 'Logger',
  Min1 = 'OneMinute',
  Min5 = 'FiveMinutes',
  Min10 = 'TenMinutes',
  Min15 = 'FifteenMinutes',
  Min30 = 'ThirtyMinutes',
  Hour1 = 'OneHour',
  Hour6 = 'SixHours',
  Hour12 = 'TwelveHours',
  Day1 = 'OneDay',
}

export enum DigitalIntervalOptionsType {
  Usage = 'Usage',
  Second = 'Second',
  Minute = 'Minute',
  Hour = 'Hour',
  Day = 'Day',
  Intensity = 'Intensity',
  Cumulative = 'Cumulative',
}

export const DigitalIntervalOptionsFn = (): Record<DigitalIntervalOptionsType, string> => ({
  [DigitalIntervalOptionsType.Usage]: t`Usage`,
  [DigitalIntervalOptionsType.Second]: t`Second`,
  [DigitalIntervalOptionsType.Minute]: t`Minute`,
  [DigitalIntervalOptionsType.Hour]: t`Hour`,
  [DigitalIntervalOptionsType.Day]: t`Day`,
  [DigitalIntervalOptionsType.Intensity]: t`Intensity`,
  [DigitalIntervalOptionsType.Cumulative]: t`Cumulative`,
});

export enum DataOptionsType {
  Primary = 'Primary',
  Secondary = 'Secondary',
  All = 'All',
}

export interface GraphUnitsModel {
  type: string;
  unit: string;
  interval?: string;
  intervalMode: IntervalOptionMode;
}

export interface GraphFiltersModel extends FiltersPeriodModel {
  interval: IntervalOptionsType;
  dataType: DataOptionsType;
  units: GraphUnitsModel[];
  showAlarmBoundariesAsPercentage?: boolean;
}

export interface FilterUnitsPostModel extends Record<string, { name: string; interval: string }> {}

export interface GraphFiltersPostModel extends Omit<GraphFiltersModel, 'units'> {
  units: FilterUnitsPostModel;
}

export interface RefreshButtonModel {
  show: boolean;
  disabled: boolean;
  onClick: () => void;
}

// - - - HISTORY & ALARMS - - -

export interface SiteOrUserModel {
  id: number;
  name: string;
}

export interface HistoryAndAlarmsFilterModel {
  sites: SiteOrUserModel[];
}

// - - - HISTORY - - -

export interface HistoryFilterModel extends HistoryParamsBaseModel, HistoryAndAlarmsFilterModel, FiltersPeriodModel {}

// - - - ALARMS - - -

export enum AlarmsAcknowledgeType {
  All = 'All',
  Acknowledged = 'Acknowledged',
  New = 'New',
}

export interface AlarmsFilterOptionsModel {
  conditions: string[];
}

export interface AlarmsFilterModel extends AlarmsParamsBaseModel, HistoryAndAlarmsFilterModel, FiltersPeriodModel {}
